import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import DateInput from '../ui/DateInput';
import { toast } from '../ui/use-toast';
import { apiRequest } from '../../config/api';
import { Calendar, Clock, User, X, Loader2 } from 'lucide-react';

const BookAppointment = ({ isOpen, onClose, patientId }) => {
  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [formData, setFormData] = useState({
    doctor_id: '',
    appointment_date: '',
    time_slot: '',
    service_type: 'Checkup',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchDoctors();
      // Reset form when dialog opens
      setFormData({
        doctor_id: '',
        appointment_date: '',
        time_slot: '',
        service_type: 'Checkup',
        notes: ''
      });
      setErrors({});
      setBookedSlots([]);
      
      // Log all appointments for all doctors to console for debugging
      logAllDoctorAppointments();
    }
  }, [isOpen]);

  // Log all appointments for all doctors (for debugging)
  const logAllDoctorAppointments = async () => {
    try {
      const data = await apiRequest('get_appointments_list.php');
      if (data.success && Array.isArray(data.appointments)) {
        console.log('=== ALL DOCTOR APPOINTMENTS ===');
        console.log('Total appointments:', data.appointments.length);
        
        // Group by doctor
        const byDoctor = {};
        data.appointments.forEach(apt => {
          const doctorId = apt.doctor_id;
          const doctorName = apt.doctor_name || `Doctor ID ${doctorId}`;
          if (!byDoctor[doctorId]) {
            byDoctor[doctorId] = {
              doctor_id: doctorId,
              doctor_name: doctorName,
              appointments: []
            };
          }
          byDoctor[doctorId].appointments.push({
            id: apt.id,
            date: apt.appointment_date,
            time: apt.appointment_time,
            status: apt.status,
            patient: apt.patient_name || 'Unknown'
          });
        });
        
        // Log grouped by doctor
        Object.values(byDoctor).forEach(doctor => {
          console.log(`\nDr. ${doctor.doctor_name} (ID: ${doctor.doctor_id}):`);
          doctor.appointments.forEach(apt => {
            console.log(`  - ${apt.date} ${apt.time} | Status: ${apt.status} | Patient: ${apt.patient}`);
          });
        });
        console.log('=== END APPOINTMENTS LOG ===\n');
      }
    } catch (error) {
      console.error('Error logging appointments:', error);
    }
  };

  // Load booked slots whenever both doctor and date are available
  useEffect(() => {
    if (isOpen && formData.doctor_id && formData.appointment_date) {
      // Clear existing slots first to avoid showing stale data
      setBookedSlots([]);
      // Load booked slots for the selected doctor and date
      loadBookedSlots(formData.appointment_date, formData.doctor_id);
    } else {
      setBookedSlots([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.doctor_id, formData.appointment_date, isOpen]);

  // Load booked time slots for a specific date
  const loadBookedSlots = async (date, doctorId) => {
    if (!date || !doctorId) {
      setBookedSlots([]);
      return;
    }
    
    // Ensure doctorId is a number
    const doctorIdNum = parseInt(doctorId, 10);
    if (isNaN(doctorIdNum)) {
      console.error('Invalid doctor ID:', doctorId);
      setBookedSlots([]);
      return;
    }
    
    console.log(`\n=== Loading booked slots ===`);
    console.log('Date:', date);
    console.log('Doctor ID:', doctorIdNum);
    
    try {
      // Use the availability check endpoint which is accessible to patients
      const url = `check_doctor_availability.php?doctor_id=${doctorIdNum}&date=${date}`;
      console.log('API URL:', url);
      const data = await apiRequest(url);
      
      console.log('API Response:', data);
      
      if (data.success && Array.isArray(data.booked_times)) {
        console.log('Raw booked times from API:', data.booked_times);
        
        // Normalize time format to HH:MM:SS
        const booked = data.booked_times
          .map(time => {
            if (!time) return null;
            
            // Normalize time format to HH:MM:SS
            // Handle different formats: HH:MM:SS, HH:MM, or H:MM:SS
            let normalizedTime = String(time).trim();
            const originalTime = normalizedTime;
            
            if (normalizedTime.length === 5) {
              // HH:MM format, add :00
              normalizedTime = normalizedTime + ':00';
            } else if (normalizedTime.length === 7 && normalizedTime.split(':').length === 3) {
              // H:MM:SS format, pad hour
              const parts = normalizedTime.split(':');
              normalizedTime = parts[0].padStart(2, '0') + ':' + parts[1] + ':' + parts[2];
            }
            
            // Ensure format is HH:MM:SS
            const timeParts = normalizedTime.split(':');
            if (timeParts.length === 3) {
              const normalized = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}:${timeParts[2].padStart(2, '0')}`;
              console.log(`  Time: ${originalTime} -> ${normalized}`);
              return normalized;
            }
            
            return normalizedTime;
          })
          .filter(time => time !== null && time.length > 0); // Remove any null or empty values
          
        console.log('Normalized booked slots:', booked);
        console.log('=== End booked slots ===\n');
        setBookedSlots(booked);
      } else {
        console.warn('API returned no booked times or error:', data);
        setBookedSlots([]);
      }
    } catch (error) {
      console.error('Error loading booked slots:', error);
      setBookedSlots([]);
    }
  };

  // Generate time slots based on day of week
  // Monday-Saturday: 9:30 AM to 9:00 PM (1-hour intervals at :30 minutes)
  // Sunday: 10:00 AM to 7:00 PM (1-hour intervals on the hour)
  const generateTimeSlots = () => {
    if (!formData.appointment_date) return [];
    
    const date = new Date(formData.appointment_date + 'T00:00:00'); // Add time to avoid timezone issues
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const slots = [];
    
    // Generate 1-hour intervals based on day
    if (dayOfWeek === 0) {
      // Sunday: 10:00 AM to 7:00 PM (hourly on the hour)
      for (let hour = 10; hour <= 19; hour++) {
        const time = `${hour.toString().padStart(2, '0')}:00:00`;
        const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        slots.push({ value: time, label: displayTime });
      }
    } else {
      // Monday-Saturday: 9:30 AM to 9:00 PM
      // First slot: 9:30 AM
      slots.push({
        value: '09:30:00',
        label: new Date('2000-01-01T09:30:00').toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
      });
      
      // Then hourly slots at :30 minutes: 10:30, 11:30, ..., 8:30
      for (let hour = 10; hour <= 20; hour++) {
        const time = `${hour.toString().padStart(2, '0')}:30:00`;
        const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        slots.push({ value: time, label: displayTime });
      }
      
      // Final slot: 9:00 PM
      slots.push({
        value: '21:00:00',
        label: new Date('2000-01-01T21:00:00').toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
      });
    }
    
    return slots;
  };

  // Normalize time format for comparison (HH:MM:SS)
  const normalizeTime = (time) => {
    if (!time) return '';
    const parts = time.split(':');
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:${(parts[2] || '00').padStart(2, '0')}`;
    }
    return time;
  };

  // Check if a time slot is booked
  const isSlotBooked = (slotValue) => {
    if (!slotValue || bookedSlots.length === 0) {
      return false;
    }
    
    const normalizedSlot = normalizeTime(slotValue);
    const isBooked = bookedSlots.some(bookedTime => {
      const normalizedBooked = normalizeTime(bookedTime);
      const matches = normalizedBooked === normalizedSlot;
      if (matches) {
        console.log(`✓ Slot ${slotValue} (normalized: ${normalizedSlot}) is BOOKED - matches ${bookedTime} (normalized: ${normalizedBooked})`);
      }
      return matches;
    });
    
    return isBooked;
  };

  // Get available slots count
  const getAvailableSlotsCount = () => {
    const allSlots = generateTimeSlots();
    const available = allSlots.filter(slot => !isSlotBooked(slot.value));
    return available.length;
  };

  const fetchDoctors = async () => {
    setLoadingDoctors(true);
    try {
      const data = await apiRequest('get_doctors_for_booking.php');
      if (data.success) {
        setDoctors(data.doctors || []);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load doctors. Please try again.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load doctors',
        variant: 'destructive'
      });
    } finally {
      setLoadingDoctors(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.doctor_id) {
      newErrors.doctor_id = 'Please select a doctor';
    }
    
    if (!formData.appointment_date) {
      newErrors.appointment_date = 'Please select a date';
    } else {
      // Check if date is in the past
      const selectedDate = new Date(formData.appointment_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.appointment_date = 'Please select a future date';
      }
      
      // Check if date is within 3 months
      const maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth() + 3);
      if (selectedDate > maxDate) {
        newErrors.appointment_date = 'Please select a date within the next 3 months';
      }
    }
    
    if (!formData.time_slot) {
      newErrors.time_slot = 'Please select a time slot';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly',
        variant: 'destructive'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const appointmentData = {
        patient_id: patientId,
        doctor_id: parseInt(formData.doctor_id),
        appointment_date: formData.appointment_date,
        appointment_time: formData.time_slot,
        service_type: formData.service_type,
        notes: formData.notes || null,
        status: 'Scheduled'
      };
      
      const data = await apiRequest('add_appointment.php', {
        method: 'POST',
        body: JSON.stringify(appointmentData)
      });
      
      if (data.success) {
        toast({
          title: 'Success',
          description: `Appointment booked successfully! Reference ID: ${data.appointment_reference_id || data.appointment?.appointment_reference_id || 'N/A'}`,
          variant: 'default'
        });
        
        // Reset form
        setFormData({
          doctor_id: '',
          appointment_date: '',
          time_slot: '',
          service_type: 'Checkup',
          notes: ''
        });
        setErrors({});
        setBookedSlots([]);
        
        // Close dialog
        onClose();
        
        // Trigger event to refresh appointments in other dashboards
        window.dispatchEvent(new CustomEvent('appointment-created'));
      } else {
        toast({
          title: 'Error',
          description: data.error || data.message || 'Failed to book appointment',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to book appointment. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get maximum date (3 months from today)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    return maxDate.toISOString().split('T')[0];
  };

  const serviceTypes = [
    'Checkup',
    'Cleaning',
    'Filling',
    'Extraction',
    'Root Canal',
    'Crown',
    'Bridge',
    'Dentures',
    'Orthodontics',
    'Other'
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-effect border-gray-700 bg-gray-900/95 backdrop-blur-sm max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-xl sm:text-2xl flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-blue-400" />
            Book Appointment
          </DialogTitle>
          <DialogDescription className="text-gray-300">
            Schedule your dental appointment with a doctor
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Doctor Selection */}
          <div className="space-y-2">
            <Label htmlFor="doctor_id" className="text-white">
              Select Doctor <span className="text-red-400">*</span>
            </Label>
            {loadingDoctors ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 text-blue-400 animate-spin mr-2" />
                <span className="text-gray-300">Loading doctors...</span>
              </div>
            ) : (
              <Select
                value={formData.doctor_id || undefined}
                onValueChange={(value) => {
                  setFormData(prev => ({ ...prev, doctor_id: value, time_slot: '' }));
                  if (errors.doctor_id) {
                    setErrors(prev => ({ ...prev, doctor_id: '' }));
                  }
                  // Clear time slot when doctor changes
                  // The useEffect will automatically load booked slots
                }}
              >
                <SelectTrigger className={`bg-gray-800 border-gray-600 text-white ${
                  errors.doctor_id ? 'border-red-500' : ''
                }`}>
                  <SelectValue placeholder="Choose a doctor" />
                </SelectTrigger>
                <SelectContent>
                  {doctors.length === 0 ? (
                    <SelectItem value="no-doctors" disabled>
                      No doctors available
                    </SelectItem>
                  ) : (
                    doctors.map((doctor) => (
                      <SelectItem key={doctor.id} value={doctor.id.toString()}>
                        <div className="flex flex-col">
                          <span className="font-medium">Dr. {doctor.name}</span>
                          {doctor.specialization && (
                            <span className="text-xs text-gray-400">{doctor.specialization}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
            {errors.doctor_id && (
              <p className="text-red-400 text-xs sm:text-sm">{errors.doctor_id}</p>
            )}
          </div>

          {/* Date Selection */}
          <div className="space-y-2">
            <Label htmlFor="appointment_date" className="text-white">
              Appointment Date <span className="text-red-400">*</span>
            </Label>
            <DateInput
              id="appointment_date"
              value={formData.appointment_date}
              onChange={(e) => {
                const date = e.target.value;
                setFormData(prev => ({ ...prev, appointment_date: date, time_slot: '' }));
                if (errors.appointment_date) {
                  setErrors(prev => ({ ...prev, appointment_date: '' }));
                }
                // Clear time slot when date changes
                // The useEffect will automatically load booked slots
              }}
              min={getMinDate()}
              max={getMaxDate()}
              required
              className={`bg-gray-800 border-gray-600 text-white ${
                errors.appointment_date ? 'border-red-500' : ''
              }`}
              placeholder="dd/mm/yyyy"
            />
            {errors.appointment_date && (
              <p className="text-red-400 text-xs sm:text-sm">{errors.appointment_date}</p>
            )}
            <p className="text-gray-400 text-xs">
              Select a date within the next 3 months (Format: dd/mm/yyyy)
            </p>
          </div>

          {/* Time Slot Selection */}
          {formData.appointment_date && formData.doctor_id && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="time_slot" className="text-white">
                  Time Slot <span className="text-red-400">*</span>
                </Label>
                <span className="text-sm text-green-400">
                  {getAvailableSlotsCount()} slots available
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-2 bg-gray-800 rounded-lg border border-gray-600">
                {(() => {
                  const allSlots = generateTimeSlots();
                  console.log(`\n=== Rendering Time Slots ===`);
                  console.log('Selected Doctor ID:', formData.doctor_id);
                  console.log('Selected Date:', formData.appointment_date);
                  console.log('Booked Slots Array:', bookedSlots);
                  console.log('Total Available Slots:', allSlots.length);
                  console.log('Booked Slots Count:', bookedSlots.length);
                  return allSlots;
                })().map((slot) => {
                  const isBooked = isSlotBooked(slot.value);
                  const isSelected = formData.time_slot === slot.value;
                  const isDisabled = isBooked;
                  
                  return (
                    <button
                      key={slot.value}
                      type="button"
                      onClick={() => {
                        if (!isDisabled) {
                          setFormData(prev => ({ ...prev, time_slot: slot.value }));
                          if (errors.time_slot) {
                            setErrors(prev => ({ ...prev, time_slot: '' }));
                          }
                        }
                      }}
                      disabled={isDisabled}
                      className={`
                        px-3 py-2 rounded text-sm font-medium transition-colors
                        ${isSelected 
                          ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white border-2 border-blue-400' 
                          : isBooked
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed line-through'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-600'
                        }
                        ${isDisabled ? 'opacity-50' : 'cursor-pointer'}
                      `}
                    >
                      {slot.label}
                    </button>
                  );
                })}
              </div>
              {errors.time_slot && (
                <p className="text-red-400 text-xs sm:text-sm">{errors.time_slot}</p>
              )}
              <p className="text-gray-400 text-xs">
                Clinic hours: Mon-Sat 9:30 AM - 9:00 PM, Sun 10:00 AM - 7:00 PM
              </p>
            </div>
          )}
          
          {(!formData.appointment_date || !formData.doctor_id) && (
            <div className="space-y-2">
              <Label className="text-white">
                Time Slot <span className="text-red-400">*</span>
              </Label>
              <div className="p-4 bg-gray-800 rounded-lg border border-gray-600 text-center">
                <p className="text-gray-400 text-sm">
                  {!formData.appointment_date && !formData.doctor_id
                    ? 'Please select a doctor and date first'
                    : !formData.appointment_date
                    ? 'Please select a date first'
                    : 'Please select a doctor first'}
                </p>
              </div>
            </div>
          )}

          {/* Service Type */}
          <div className="space-y-2">
            <Label htmlFor="service_type" className="text-white">
              Service Type
            </Label>
            <Select
              value={formData.service_type || undefined}
              onValueChange={(value) => setFormData(prev => ({ ...prev, service_type: value }))}
            >
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                <SelectValue placeholder="Select service type" />
              </SelectTrigger>
              <SelectContent>
                {serviceTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-white">
              Additional Notes (Optional)
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any specific concerns or requests..."
              className="bg-gray-800 border-gray-600 text-white"
              rows={3}
            />
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="w-full sm:w-auto bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || loadingDoctors}
              className="w-full sm:w-auto medical-gradient text-white hover:opacity-90"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Booking...
                </>
              ) : (
                <>
                  <Calendar className="h-4 w-4 mr-2" />
                  Book Appointment
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookAppointment;

